import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as inboxApi from '../api/inbox';
import { usePagedList } from '../lib/usePagedList';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  PagedListFooter,
  Field,
  TextareaField,
  RelativeTime,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const PAGE_SIZE = 25;

const EMPTY_COMPOSER = { recipientId: '', subject: '', body: '' };

const TYPE_BADGE = {
  direct: null,
  invite: { label: 'Invite', tone: 'info' },
  subscription: { label: 'Subscription', tone: 'warning' },
  system: { label: 'System', tone: 'neutral' },
};

const PILL_BY_TONE = { info: ui.pillInfo, warning: ui.pillWarning, neutral: ui.pillNeutral };

function MessageRow({ message: m, userId, onMarkRead, onArchive }) {
  const badge = TYPE_BADGE[m.type];
  return (
    <div className={cn(ui.card, 'mb-4')}>
      <div className={cn(ui.row, ui.spread)}>
        <span className="flex flex-wrap items-center gap-2 *:min-w-0">
          {m.status === 'sent' && <span className={ui.unreadDot} aria-hidden="true" />}
          {badge && <span className={PILL_BY_TONE[badge.tone]}>{badge.label}</span>}
          <strong>{m.subject || '(no subject)'}</strong>
        </span>
        <RelativeTime iso={m.createdAt} />
      </div>
      <p className="my-1">
        {m.type === 'system'
          ? 'System notification'
          : m.senderId === userId
            ? `To: ${m.recipientId}`
            : `From: ${m.senderId}`}
      </p>
      <p>{m.body && m.body.length > 200 ? m.body.slice(0, 200) + '...' : m.body}</p>
      <div className="flex flex-wrap items-center gap-2 *:min-w-0">
        {m.status === 'sent' && m.recipientId === userId && (
          <button
            type="button"
            className={ui.btnSecondary}
            onClick={e => onMarkRead(m.messageId, e)}
          >
            Mark read
          </button>
        )}
        {m.status !== 'archived' && (
          <button
            type="button"
            className={ui.btnSecondary}
            onClick={e => onArchive(m.messageId, e)}
          >
            Archive
          </button>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [view, setView] = useState('conversations');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composer, setComposer] = useState(EMPTY_COMPOSER);
  const [composerError, setComposerError] = useState(null);
  const [composerBusy, setComposerBusy] = useState(false);

  const conversations = usePagedList(
    cursor =>
      inboxApi
        .listConversations({
          limit: PAGE_SIZE,
          exclusiveStartKey: cursor ? encodeURIComponent(JSON.stringify(cursor)) : undefined,
        })
        .then(d => ({ items: d.items || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [view === 'conversations']
  );

  const inbox = usePagedList(
    cursor =>
      inboxApi
        .listInbox({
          limit: PAGE_SIZE,
          exclusiveStartKey: cursor ? encodeURIComponent(JSON.stringify(cursor)) : undefined,
        })
        .then(d => ({ items: d.items || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [view === 'inbox']
  );

  function openComposer() {
    setComposer(EMPTY_COMPOSER);
    setComposerError(null);
    setComposerOpen(true);
  }

  function closeComposer() {
    setComposerOpen(false);
    setComposerError(null);
  }

  async function submitComposer(event) {
    event.preventDefault();
    if (!composer.recipientId.trim()) {
      setComposerError(new Error('Recipient ID is required.'));
      return;
    }
    if (!composer.body.trim()) {
      setComposerError(new Error('Message body is required.'));
      return;
    }
    setComposerBusy(true);
    setComposerError(null);
    try {
      await inboxApi.sendMessage({
        recipientId: composer.recipientId.trim(),
        subject: composer.subject.trim() || undefined,
        body: composer.body.trim(),
      });
      toast.success('Message sent.');
      closeComposer();
      conversations.reload();
    } catch (err) {
      setComposerError(err);
      toast.error(err);
    } finally {
      setComposerBusy(false);
    }
  }

  async function handleMarkRead(messageId, event) {
    event.stopPropagation();
    try {
      await inboxApi.markRead(messageId);
      inbox.mutateItem(messageId, { status: 'read' }, 'messageId');
    } catch (err) {
      toast.error(err);
    }
  }

  async function handleArchive(messageId, event) {
    event.stopPropagation();
    try {
      await inboxApi.archiveMessage(messageId);
      inbox.removeItem(messageId, 'messageId');
    } catch (err) {
      toast.error(err);
    }
  }

  function openConversation(conversationId) {
    navigate(`/messages/${conversationId}`);
  }

  const activeList = view === 'conversations' ? conversations : inbox;

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className="text-title-l">Messages</h1>
        <button type="button" className={ui.btnPrimary} onClick={openComposer}>
          New message
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 *:min-w-0">
        <button
          type="button"
          className={view === 'conversations' ? ui.btnPrimary : ui.btnSecondary}
          onClick={() => setView('conversations')}
        >
          Conversations
        </button>
        <button
          type="button"
          className={view === 'inbox' ? ui.btnPrimary : ui.btnSecondary}
          onClick={() => setView('inbox')}
        >
          All messages
        </button>
      </div>

      <ErrorAlert error={activeList.error} />

      {activeList.loading ? (
        <Spinner />
      ) : activeList.items.length === 0 ? (
        !activeList.error && (
          <EmptyState>
            {view === 'conversations'
              ? 'No conversations yet. Send a message to get started.'
              : 'No messages.'}
          </EmptyState>
        )
      ) : view === 'conversations' ? (
        conversations.items.map(c => (
          <div
            key={c.conversationId}
            className={cn(ui.cardClickable, 'mb-4')}
            role="button"
            tabIndex={0}
            onClick={() => openConversation(c.conversationId)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openConversation(c.conversationId);
              }
            }}
          >
            <div className={cn(ui.row, ui.spread)}>
              <span className={ui.row}>
                {c.unreadCount > 0 && <span className={ui.unreadDot} aria-hidden="true" />}
                <strong>
                  {c.participantIds.filter(id => id !== user?.userId).join(', ') || 'Conversation'}
                </strong>
              </span>
              <RelativeTime iso={c.lastMessageAt} />
            </div>
            {c.lastMessagePreview && <p className={cn(ui.muted, 'mt-1')}>{c.lastMessagePreview}</p>}
            {c.unreadCount > 0 && (
              <span className={cn(ui.pillPrimary, 'mt-1')}>{c.unreadCount} unread</span>
            )}
          </div>
        ))
      ) : (
        inbox.items.map(m => (
          <MessageRow
            key={m.messageId}
            message={m}
            userId={user?.userId}
            onMarkRead={handleMarkRead}
            onArchive={handleArchive}
          />
        ))
      )}

      <PagedListFooter
        hasMore={activeList.hasMore}
        loadingMore={activeList.loadingMore}
        onLoadMore={activeList.loadMore}
        endLabel={activeList.items.length ? 'No more items.' : null}
      />

      <Modal open={composerOpen} onClose={closeComposer} title="New message">
        <form onSubmit={submitComposer}>
          <ErrorAlert error={composerError} />
          <Field
            label="Recipient ID"
            required
            value={composer.recipientId}
            onChange={e => setComposer(c => ({ ...c, recipientId: e.target.value }))}
          />
          <Field
            label="Subject"
            value={composer.subject}
            onChange={e => setComposer(c => ({ ...c, subject: e.target.value }))}
          />
          <TextareaField
            label="Message"
            rows={4}
            required
            value={composer.body}
            onChange={e => setComposer(c => ({ ...c, body: e.target.value }))}
          />
          <div className={cn(ui.row, 'mt-4')}>
            <button type="button" className={ui.btnSecondary} onClick={closeComposer}>
              Cancel
            </button>
            <button type="submit" className={ui.btnPrimary} disabled={composerBusy}>
              {composerBusy ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
